/**
 * Shared types + pure helpers for the config-driven landing page.
 *
 * IMPORTANT: everything in this module is universal (no 'use client', no
 * server-only imports) so the same block components render server-side on the
 * public tenant page AND client-side inside the settings builder preview.
 */

import { isBundledLandingImage } from '~/(tenant)/_lib/schema/landing-assets';

export interface LandingService {
  name: string;
  description?: string;
}

export interface LandingLocation {
  id: string;
  name: string;
  city: string | null;
  neighborhood: string | null;
  /** Ordered full public URLs (facility_images stores complete URLs). Every
   *  one is rendered as a gallery — never just the first. */
  imageUrls: string[];
  address: string | null;
  googleMapsUrl: string | null;
  /** Pre-baked embeddable map URL (court_locations.google_maps_embed_url). */
  googleMapsEmbedUrl: string | null;
}

export interface LandingCoach {
  id: string;
  name: string;
  pictureUrl: string | null;
}

export interface LandingContact {
  phone?: string;
  email?: string;
  whatsapp?: string;
}

/** Normalized from loadAvailableMembershipPlans (admin-client loader). */
export interface LandingMembershipPlan {
  id: string;
  name: string;
  description: string | null;
  /** Secondary-language description (typically Arabic). */
  descriptionAlt: string | null;
  /** membership_period enum: monthly | quarterly | semi_annual | annual | lifetime */
  period: string;
  price: number;
  benefits: string[];
  benefitsAlt: string[];
  /** Full public URL (membership_plan_images bucket) — render directly. */
  imageUrl: string | null;
}

/** Normalized from loadAvailablePackages (admin-client loader). */
export interface LandingPackage {
  id: string;
  name: string;
  description: string | null;
  descriptionAlt: string | null;
  bookingCount: number;
  price: number;
  originalPrice: number | null;
  validityDays: number | null;
  validityMonths: number;
  features: string[];
  featuresAlt: string[];
}

export interface LandingRenderContext {
  wordmark?: string;
  /** NEXT_PUBLIC_SUPABASE_URL — base for home_assets public URLs. */
  storageUrl: string;
  accountId: string;
  accountSlug?: string;
  timeZone?: string;
  /** Active locale ('en' | 'ar' | ...). Drives the `_ar` field fallback. */
  locale: string;
  businessName: string;
  /** From business_info.services — feeds the services block in 'auto' mode. */
  services: LandingService[];
  contact: LandingContact | null;
  operatingHours: Record<string, { open: string; close: string }> | null;
  address: string | null;
  googleMapsUrl: string | null;
  /** Pre-baked embeddable map URL for the primary location, when available. */
  googleMapsEmbedUrl: string | null;
  locations: LandingLocation[];
  coaches: LandingCoach[];
  membershipPlans: LandingMembershipPlan[];
  packages: LandingPackage[];
  /** Resolved from config.theme by the renderer root. */
  dark: boolean;
  serif: boolean;
  /** Body-copy size tier, resolved from config.theme.textScale ('md' default). */
  textScale: 'sm' | 'md' | 'lg';
}

const priceFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

/**
 * Digits only — the currency symbol is <CurrencyIcon />'s job (see the
 * currency-rendering rules in CLAUDE.md).
 */
export function formatPrice(value: number) {
  return priceFormatter.format(value);
}

/**
 * Builds the public URL for an image uploaded via the builder into the
 * home_assets bucket. imageKey may carry a `?v=<ts>` cache-bust suffix.
 */
export function landingImageUrl(ctx: Pick<LandingRenderContext, 'storageUrl' | 'accountId'>, imageKey: string) {
  if (isBundledLandingImage(imageKey)) return imageKey;
  if (!/^(?!\.{1,2}(?:\?|$))[\w.-]+(?:\?v=\d+)?$/.test(imageKey)) return '';
  return `${ctx.storageUrl}/storage/v1/object/public/home_assets/${ctx.accountId}/${imageKey}`;
}

/**
 * Locale fallback for tenant-authored copy. Local pure duplicate of
 * `native/_lib/use-localized.ts`'s pickLocalized — that module is marked
 * 'use client', so importing it here would break server rendering.
 */
export function pickLocalized(
  value: string | undefined,
  valueAr: string | undefined,
  locale: string,
): string | undefined {
  if (locale?.split('-')[0] === 'ar' && valueAr && valueAr.trim() !== '') {
    return valueAr;
  }
  return value;
}

/**
 * Muted/body text tone matched to the block theme (not the app theme).
 * Opacity-based so it inherits the exact ink color — works on the default
 * white/near-black canvases AND the palette presets (bone, navy, forest…).
 */
export function mutedText(dark: boolean) {
  return dark ? 'opacity-70' : 'opacity-65';
}

/**
 * Card surface matched to the block theme. Translucent so cards sit
 * naturally on any palette canvas, not just pure white/black.
 */
export function cardSurface(dark: boolean) {
  return dark
    ? 'landing-card border border-white/10 bg-white/[0.05]'
    : 'landing-card border border-black/10 bg-white/70 shadow-sm';
}

/** Hairline divider tone for the block theme. */
export function hairline(dark: boolean) {
  return dark ? 'border-white/10' : 'border-black/10';
}

/** Heading font — serif themes get an editorial look. */
export function headingFont(serif: boolean) {
  return serif ? 'font-serif' : '';
}

/**
 * Body-copy size for the main prose spots (Settings → Website → Theme → Text
 * size). Returns the FULL text-size class (including the 'md' default) so it
 * replaces the literal `text-base` at the call site — deterministic regardless
 * of whether cn() tailwind-merges.
 */
export function bodyTextSize(scale: 'sm' | 'md' | 'lg') {
  return scale === 'sm' ? 'text-sm' : scale === 'lg' ? 'text-lg' : 'text-base';
}

/**
 * Secondary copy (card descriptions, bios, FAQ answers, quotes) — one step
 * below `bodyTextSize` at every tier, so the whole page scales together.
 * Without this the setting only moved three paragraphs and a tenant who chose
 * "Large" for readability still got 14px FAQ answers and service descriptions.
 * Returns the FULL class for the same reason `bodyTextSize` does.
 */
export function secondaryTextSize(scale: 'sm' | 'md' | 'lg') {
  return scale === 'sm' ? 'text-xs' : scale === 'lg' ? 'text-base' : 'text-sm';
}

// NOTE: the accent → readable-foreground helper is the shared, client-safe
// `deriveContrastingForeground` in `~/lib/utils/color-derivation` — the SAME one
// the tenant brand color uses (get-branding-style.ts). The renderer imports it
// directly, so the landing accent and the brand color can never drift apart.
